import { useEffect, useMemo, useRef, useState } from 'react';
import axiosInstance from '../utils/axiosInstance';

const responseCache = new Map();
const pendingRequests = new Map();
const CACHE_TTL = 15000;

const getDirectory = (endpoint, params) => {
  const key = `${endpoint}:${JSON.stringify(params)}`;
  const cached = responseCache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) return Promise.resolve(cached.data);
  if (pendingRequests.has(key)) return pendingRequests.get(key);

  const request = axiosInstance(endpoint, 'get', params)
    .then((response) => {
      if (!response || response.status >= 400) throw new Error(response?.data?.message || 'Unable to load directory');
      responseCache.set(key, { data: response.data, timestamp: Date.now() });
      return response.data;
    })
    .finally(() => pendingRequests.delete(key));
  pendingRequests.set(key, request);
  return request;
};

export const invalidateEntityDirectory = (endpoint) => {
  [...responseCache.keys()].forEach((key) => {
    if (key.startsWith(`${endpoint}:`)) responseCache.delete(key);
  });
};

export const useEntityDirectory = ({ endpoint, page, pageSize, keyword, refreshKey = 0 }) => {
  const [debouncedKeyword, setDebouncedKeyword] = useState(keyword);
  const [state, setState] = useState({ data: [], meta: {}, incomes: 0, expenses: 0, loading: true, error: '' });
  const requestId = useRef(0);

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedKeyword(keyword.trim()), 250);
    return () => window.clearTimeout(timeout);
  }, [keyword]);

  const params = useMemo(() => ({ pageNum: page, pageSize, keyword: debouncedKeyword }), [debouncedKeyword, page, pageSize]);

  useEffect(() => {
    let active = true;
    const id = ++requestId.current;
    setState((current) => ({ ...current, loading: true, error: '' }));
    getDirectory(endpoint, params)
      .then((result) => {
        if (!active || id !== requestId.current) return;
        setState({
          data: result.data || [],
          meta: result.meta || {},
          incomes: Number(result.incomes) || 0,
          expenses: Number(result.expenses) || 0,
          loading: false,
          error: '',
        });
      })
      .catch((error) => {
        if (!active || id !== requestId.current) return;
        setState((current) => ({ ...current, loading: false, error: error.message }));
      });
    return () => { active = false; };
  }, [endpoint, params, refreshKey]);

  return state;
};
